import {
  type ComponentProps, useCallback, useMemo,
} from 'react';
import type {
  ColumnId, SpreadsheetColumnId,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import type { SpreadsheetId } from '~/_shared/types/spreadsheetData/spreadsheetRow';
import { getColumnName } from '~/_shared/utils/metric/defaultMetric.helpers';
import {
  notNull, notNullOrUndefined,
} from '~/_shared/utils/typeGuards';
import { LOCATION_COUNT_ID } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/modalSteps/constraintsValidation/constraints.repository';
import { useGetColumnATOMetrics } from '~/sidebar/sidebarApps/mapTools/boundary/loadBoundaryPane/createCustomBoundaryPane/createBoundaryGroupFromAIModal/useGetColumnATOMetrics';
import type { DemographicIdCode } from '~/store/boundaryGroups/boundaryGroups.repository';
import {
  useBoundaryGroupsSelector, useSelectAllDemographicsMap,
} from '~/store/boundaryGroups/boundaryGroups.selectors';
import {
  type ChartMetric, ChartMetricType, isChartMetricDemographic, isChartMetricLocationCount, isChartMetricSpreadsheetColumn,
} from '~/store/mapSettings/data/charts/mapSettingsCharts.types';
import { useMatchupDataSelector } from '~/store/matchupData/matchupData.selectors';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import type { GroupId } from '~/store/spreadsheetData/spreadsheetData.state';
import {
  type ChartEditorData, ChartEditorModalComponent,
} from './chartEditorModal.component';
import { useBoundaryGroupsChartOptions } from './useBoundaryGroupsChartOptions';

type ColumnOption = Readonly<{
  name: string;
  value: string;
  column: SpreadsheetColumnId;
}>;

type LocationCountColumnOption = ColumnOption & Readonly<{
  groupValues: ReadonlyArray<{ name: string; value: GroupId }>;
}>;

export type ChartEditorModalContainerProps = Omit<ComponentProps<typeof ChartEditorModalComponent>,
  'dataColumnOptions' | 'locationCountColumnOptions' | 'onSave' | 'defaultMetrics' | 'demographicsMap'
> & Readonly<{
  showBoundaryGroupSelector?: boolean;
  defaultMetrics?: ReadonlyArray<ChartMetric>;
  onSave: (params: Omit<ChartEditorData, 'metrics'> & {
    metrics: ReadonlyArray<{
      type: ChartMetricType;
      spreadsheetId?: SpreadsheetId;
      columnId?: ColumnId;
      demographic?: DemographicIdCode;
      columnValue?: GroupId;
    }>;
  }) => void;
}>;

export const ChartEditorModalContainer = ({
  defaultMetrics,
  defaultBoundaryGroupId,
  showBoundaryGroupSelector,
  onSave,
  ...restProps
}: ChartEditorModalContainerProps) => {
  const matchupData = useMatchupDataSelector();
  const boundaryGroups = useBoundaryGroupsSelector();
  const allDemographicsMap = useSelectAllDemographicsMap();
  const boundaryGroupOptions = useBoundaryGroupsChartOptions();
  const spreadsheetId = usePrimarySpreadsheetId();
  const { columnMetrics, isLoading: isLoadingColumnsInfo } = useGetColumnATOMetrics(spreadsheetId, { includeGroups: true });

  // Numeric columns are the available options for ChartMetricType.SpreadsheetColumn metrics.
  const numericColumnOptions = useMemo<ReadonlyArray<ColumnOption>>(() => {
    if (!spreadsheetId) {
      return [];
    }
    return columnMetrics
      .filter(metric => metric.isNumeric && metric.columnId !== LOCATION_COUNT_ID)
      .map(metric => ({
        name: metric.name,
        value: `numerical-${spreadsheetId}-${metric.columnId}`,
        column: { spreadsheetId, columnId: metric.columnId },
      }));
  }, [columnMetrics, spreadsheetId]);

  // Columns that have group values are the available options for ChartMetricType.LocationCount metrics,
  // together with the possible column values (groups) for each of those columns.
  const locationCountColumnOptions = useMemo<ReadonlyArray<LocationCountColumnOption>>(() => {
    if (!spreadsheetId) {
      return [];
    }
    const groupValuesByColumnId = new Map<ColumnId, Array<{ name: string; value: GroupId }>>();
    for (const metric of columnMetrics) {
      if (metric.columnId === LOCATION_COUNT_ID || metric.groupId === undefined) {
        continue;
      }
      const groupValues = groupValuesByColumnId.get(metric.columnId) ?? [];
      groupValues.push({ name: metric.name, value: metric.groupId });
      groupValuesByColumnId.set(metric.columnId, groupValues);
    }
    return Array.from(groupValuesByColumnId.entries()).map(([columnId, groupValues]) => ({
      name: getColumnName(matchupData, spreadsheetId, columnId),
      value: `location-count-${spreadsheetId}-${columnId}`,
      column: { spreadsheetId, columnId },
      groupValues,
    }));
  }, [columnMetrics, matchupData, spreadsheetId]);

  const defaultMetricsData = useMemo(() => defaultMetrics ? (
    defaultMetrics.map(metric => {
      if (isChartMetricSpreadsheetColumn(metric)) {
        const matchedOption = numericColumnOptions.find(option =>
          compareSpreadsheetColumnIds(option.column, metric.column)
        );
        if (matchedOption) {
          return {
            type: ChartMetricType.SpreadsheetColumn,
            dataColumn: matchedOption.value,
            demographic: null,
            columnValue: null,
          };
        }
      }
      if (isChartMetricDemographic(metric)) {
        return {
          type: ChartMetricType.Demographic,
          dataColumn: null,
          demographic: metric.demographicId,
          columnValue: null,
        };
      }
      if (isChartMetricLocationCount(metric)) {
        // Count of all locations in the spreadsheet.
        if (!metric.column) {
          return {
            type: ChartMetricType.LocationCount,
            dataColumn: null,
            demographic: null,
            columnValue: null,
          };
        }
        // Count of locations that have a specific value in a specific column.
        const metricColumn = metric.column;
        const matchedColumn = locationCountColumnOptions.find(option =>
          compareSpreadsheetColumnIds(option.column, metricColumn)
        );
        const matchedColumnValue = matchedColumn?.groupValues.find(group => group.value === metric.columnValue);
        if (matchedColumn && matchedColumnValue) {
          return {
            type: ChartMetricType.LocationCount,
            dataColumn: matchedColumn.value,
            demographic: null,
            columnValue: matchedColumnValue.value ?? null,
          };
        }
      }
      return null;
    }).filter(notNull)
  ) : undefined, [numericColumnOptions, locationCountColumnOptions, defaultMetrics]);

  const demographicsMap = useMemo(() => {
    const geographicBoundaryGroupsDemographics = boundaryGroups.map((group) => ([
      group.id, group.demographics || [],
    ] as const));

    const defaultBoundaryGroupDemographics = defaultBoundaryGroupId && defaultMetrics
      ? [[
        defaultBoundaryGroupId, defaultMetrics
          .filter(isChartMetricDemographic)
          .map(metric => allDemographicsMap.get(metric.demographicId))
          .filter(notNullOrUndefined)
          .flat(),
      ]] as const
      : [];

    return new Map(geographicBoundaryGroupsDemographics.concat(defaultBoundaryGroupDemographics));
  }, [allDemographicsMap, boundaryGroups, defaultBoundaryGroupId, defaultMetrics]);

  const handleSave = useCallback((params: ChartEditorData) => {
    onSave({
      ...params,
      metrics: params.metrics.map(metric => {
        if (metric.type === ChartMetricType.SpreadsheetColumn && metric.dataColumn) {
          const matchedOption = numericColumnOptions.find(option => option.value === metric.dataColumn);
          if (matchedOption) {
            return {
              type: ChartMetricType.SpreadsheetColumn,
              columnId: matchedOption.column.columnId,
              spreadsheetId: matchedOption.column.spreadsheetId,
            };
          }
        }
        if (metric.type === ChartMetricType.Demographic && metric.demographic) {
          return { type: ChartMetricType.Demographic, demographic: metric.demographic };
        }
        if (metric.type === ChartMetricType.LocationCount && spreadsheetId) {
          // Count of all locations in the spreadsheet.
          if (!metric.dataColumn) {
            return { type: ChartMetricType.LocationCount, spreadsheetId };
          }
          // Count of locations that have a specific value in a specific column.
          const matchedOption = locationCountColumnOptions.find(option => option.value === metric.dataColumn);
          if (matchedOption && metric.columnValue) {
            return {
              type: ChartMetricType.LocationCount,
              spreadsheetId: matchedOption.column.spreadsheetId,
              columnId: matchedOption.column.columnId,
              columnValue: metric.columnValue,
            };
          }
        }
        return null;
      }).filter(notNullOrUndefined),
    });
    restProps.onClose();
  }, [locationCountColumnOptions, numericColumnOptions, onSave, restProps, spreadsheetId]);

  return (
    <ChartEditorModalComponent
      {...restProps}
      boundaryGroupOptions={showBoundaryGroupSelector ? boundaryGroupOptions : undefined}
      dataColumnOptions={numericColumnOptions}
      locationCountColumnOptions={locationCountColumnOptions}
      defaultMetrics={defaultMetricsData}
      defaultBoundaryGroupId={defaultBoundaryGroupId}
      demographicsMap={demographicsMap}
      isLoading={isLoadingColumnsInfo}
      onSave={handleSave}
    />
  );
};
